import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Clock, 
  Activity, 
  Thermometer, 
  Droplets, 
  Heart, 
  Stethoscope, 
  FileText, 
  ShieldCheck,
  ChevronRight,
  Loader2,
  Phone,
  Mail,
  Scale,
  Wind,
  Zap,
  Info,
  Sparkles
} from 'lucide-react';
import { ConsultDetails, Health, PatientProfile, Vitals, History } from '../../components/interface/SummaryInterface';
import { BASE_URL } from '../../components/interface/Config';
import { apiHandler } from '../../services/authService';
import { motion } from 'framer-motion';
import { AmbientAssistant } from '../../components/AmbientAI/AmbientAssistant';

const TeleconsultSummary: React.FC = () => {
  const navigate = useNavigate();
  const { publishertoken } = useParams<{ publishertoken: string }>();
  
  const [profile, setProfile] = useState<PatientProfile | null>(null);
  const [consultDetails, setConsultDetails] = useState<ConsultDetails | null>(null);
  const [mrn, setMrn] = useState<string | null>(null);
  const [age, setAge] = useState<string | null>(null);
  const [vitalsData, setVitalsData] = useState<Vitals | null>(null);
  const [historyData, setHistoryData] = useState<History | null>(null);
  const [healthData, setHealthData] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const url = `${BASE_URL}/patient/summary?token=${publishertoken}&pdf_for=consult_report`;
      const response = await apiHandler('', 'GET', {}, url);
      
      if (response?.data) {
        const data = response.data.data;
        setProfile(data.profile.a_profile);
        setMrn(data.profile.b_patient.additional_info.mrn_number);
        setAge(data.profile.b_patient.additional_info.age);
        setConsultDetails(data.profile.b_consult_details);
        setVitalsData(data.vitals || null);
        setHealthData(data.health || null);
        setHistoryData(data.history || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    if (publishertoken) fetchProfile(true);
  }, [publishertoken]);

  useEffect(() => {
    const handleRefresh = () => {
      if (publishertoken) fetchProfile();
    };
    window.addEventListener("refresh-soap-notes", handleRefresh);
    return () => {
      window.removeEventListener("refresh-soap-notes", handleRefresh);
    };
  }, [publishertoken]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "New": return { color: "bg-blue-500", text: "New" };
      case "Started": return { color: "bg-amber-500", text: "In Progress" };
      case "Ended": return { color: "bg-emerald-500", text: "Completed" };
      case "Cancelled": return { color: "bg-rose-500", text: "Cancelled" };
      default: return { color: "bg-slate-400", text: status };
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
        <p className="text-slate-500 font-bold tracking-widest uppercase text-[10px] md:text-xs">Generating Clinical Summary...</p>
      </div>
    );
  }

  const status = getStatusConfig(consultDetails?.consult_status?.name || "");

  // Extract patient (subscriber) ref_number from consult participants
  const subscriberPatientId = consultDetails?.participants?.find(
    (p: any) => p.role === "subscriber"
  )?.ref_number || null;

  const subscriberName = `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim();

  return (
    <>
    <div className="min-h-screen bg-slate-50/50 font-primary">
      {/* Sticky Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-4 md:px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3 md:gap-4 overflow-hidden">
          <button 
            onClick={() => navigate(-1)}
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-xl md:rounded-2xl bg-slate-100 text-slate-600 hover:bg-primary hover:text-white transition-all shadow-sm border border-slate-200 shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="truncate">
            <h1 className="text-base md:text-xl font-black text-slate-900 tracking-tight leading-none mb-1 truncate">Consultation Report</h1>
            <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">Ref: {publishertoken?.slice(0, 8)}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Ambient AI Button */}
          {subscriberPatientId && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-ambient-ai', {
                  detail: { patientId: subscriberPatientId, patientName: subscriberName }
                }));
              }}
              className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 text-white text-[10px] md:text-xs font-black uppercase tracking-widest shadow-lg hover:shadow-indigo-500/30 transition-all border border-white/20"
            >
              <Sparkles size={13} className="animate-pulse shrink-0" />
              <span className="hidden sm:inline">Ambient AI</span>
            </motion.button>
          )}

          <div className={`px-3 md:px-4 py-1.5 rounded-full ${status.color} text-white text-[9px] md:text-[11px] font-black uppercase tracking-widest shadow-lg shrink-0`}>
            {status.text}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Patient Profile Card */}
        <section className="bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden flex flex-col md:flex-row">
          <div className="bg-primary p-6 md:p-8 text-white flex flex-col justify-center items-center md:items-start md:w-80">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/20 border-4 border-white/20 flex items-center justify-center mb-4 backdrop-blur-md shrink-0">
              <User size={48} className="text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-black mb-1 text-center md:text-left">{profile?.first_name} {profile?.last_name}</h2>
            <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm font-bold mb-4">
              <span>{profile?.gender}</span>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span>{age} Years</span>
            </div>
            <div className="w-full space-y-2 mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-[11px] font-medium">
                <ShieldCheck size={14} className="text-white/60" />
                <span>MRN: {mrn || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] font-medium">
                <Phone size={14} className="text-white/60" />
                <span>{profile?.mobile || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] font-medium overflow-hidden">
                <Mail size={14} className="text-white/60 shrink-0" />
                <span className="truncate">{profile?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="flex-grow p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 h-full">
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Info size={14} /> Consultation Reason
                </h3>
                <p className="text-slate-800 font-bold text-base md:text-lg leading-relaxed">
                  {consultDetails?.reason || "No reason provided for this consultation."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="p-4 rounded-2xl md:rounded-3xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Calendar size={14} />
                    <span className="text-[9px] font-black uppercase">Date</span>
                  </div>
                  <p className="text-xs md:text-sm font-bold text-slate-700">
                    {consultDetails?.scheduled_at ? format(new Date(consultDetails.scheduled_at), 'dd MMM yyyy') : 'N/A'}
                  </p>
                </div>
                <div className="p-4 rounded-2xl md:rounded-3xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Clock size={14} />
                    <span className="text-[9px] font-black uppercase">Time</span>
                  </div>
                  <p className="text-xs md:text-sm font-bold text-slate-700">
                    {consultDetails?.scheduled_at ? format(new Date(consultDetails.scheduled_at), 'hh:mm a') : 'N/A'}
                  </p>
                </div>
                <div className="col-span-2 p-4 rounded-2xl md:rounded-3xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-400 mb-2">
                    <Activity size={14} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Session Duration</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                    <span className="text-[11px] font-bold text-slate-500">Started: {consultDetails?.started_at ? format(new Date(consultDetails.started_at), 'HH:mm') : '--'}</span>
                    <ChevronRight size={14} className="text-slate-300 hidden sm:block" />
                    <span className="text-[11px] font-bold text-slate-500">Ended: {consultDetails?.ended_at ? format(new Date(consultDetails.ended_at), 'HH:mm') : '--'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vital Signs Grid */}
        <section>
          <div className="flex items-center gap-4 mb-6">
            <div className="p-2 bg-rose-500/10 rounded-xl md:rounded-2xl">
              <Activity className="text-rose-500 w-5 h-5 md:w-6 md:h-6" />
            </div>
            <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">Vital Signs & Biometrics</h3>
            <div className="h-px flex-grow bg-slate-100" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {/* BMI Card */}
            {vitalsData?.a_bmi && vitalsData.a_bmi.length > 0 && (
              <div className="bg-white p-5 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 shadow-sm group hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="p-2.5 md:p-3 bg-emerald-50 rounded-xl md:rounded-2xl group-hover:scale-110 transition-transform">
                    <Scale className="text-emerald-500 w-4.5 h-4.5 md:w-5 md:h-5" />
                  </div>
                  <span className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">BMI Index</span>
                </div>
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-3xl md:text-4xl font-black text-slate-900">{vitalsData.a_bmi[0].details.bmi || '-'}</span>
                  <span className="text-slate-400 font-bold mb-1 text-xs md:text-sm">kg/m²</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div 
                    className="px-3 py-1 rounded-full text-[9px] md:text-[10px] font-black text-white uppercase tracking-widest w-fit"
                    style={{ backgroundColor: vitalsData.a_bmi[0].details.range_code || '#cbd5e1' }}
                  >
                    {vitalsData.a_bmi[0].details.bmiFlag || 'Unknown'}
                  </div>
                  <span className="text-[10px] md:text-xs text-slate-400 font-medium whitespace-nowrap">H: {vitalsData.a_bmi[0].details.height}cm • W: {vitalsData.a_bmi[0].details.weight}kg</span>
                </div>
              </div>
            )}

            {/* Temperature Card */}
            {vitalsData?.b_temperature && vitalsData.b_temperature.length > 0 && (
              <div className="bg-white p-5 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 shadow-sm group hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="p-2.5 md:p-3 bg-orange-50 rounded-xl md:rounded-2xl group-hover:scale-110 transition-transform">
                    <Thermometer className="text-orange-500 w-4.5 h-4.5 md:w-5 md:h-5" />
                  </div>
                  <span className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">Temperature</span>
                </div>
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-3xl md:text-4xl font-black text-slate-900">{vitalsData.b_temperature[0].details.temperature || '-'}</span>
                  <span className="text-slate-400 font-bold mb-1 text-xs md:text-sm">°F</span>
                </div>
                <div className="px-3 py-1.5 rounded-xl md:rounded-2xl bg-slate-50 border border-slate-100 text-[10px] md:text-xs font-bold text-slate-600 inline-flex items-center gap-2 w-fit">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: vitalsData.b_temperature[0].details.range_code || '#cbd5e1' }} />
                  {vitalsData.b_temperature[0].details.temperatureFlag || 'Normal'}
                </div>
              </div>
            )}

            {/* Blood Pressure Card */}
            {vitalsData?.f_bp && vitalsData.f_bp.length > 0 && (
              <div className="bg-white p-5 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 shadow-sm group hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="p-2.5 md:p-3 bg-rose-50 rounded-xl md:rounded-2xl group-hover:scale-110 transition-transform">
                    <Heart className="text-rose-500 w-4.5 h-4.5 md:w-5 md:h-5" />
                  </div>
                  <span className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest">Blood Pressure</span>
                </div>
                <div className="flex items-end gap-2 mb-4">
                  <span className="text-2xl md:text-4xl font-black text-slate-900 truncate">
                    {vitalsData.f_bp[0].details.systolic}/{vitalsData.f_bp[0].details.diastolic}
                  </span>
                  <span className="text-slate-400 font-bold mb-1 text-xs md:text-sm shrink-0">mmHg</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                   <div className="px-3 py-1.5 rounded-xl md:rounded-2xl bg-slate-50 border border-slate-100 text-[10px] md:text-xs font-bold text-slate-600 inline-flex items-center gap-2 w-fit">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: vitalsData.f_bp[0].details.range_code || '#cbd5e1' }} />
                    {vitalsData.f_bp[0].details.bpFlag || 'Normal'}
                  </div>
                  <div className="flex items-center gap-1 text-slate-400 font-bold text-[9px] md:text-[10px]">
                    <Zap size={10} className="text-amber-500" /> Pulse: {vitalsData.f_bp[0].details.pulse}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Clinical History Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Medical History */}
          <section>
            <div className="flex items-center gap-4 mb-4 md:mb-6">
              <div className="p-2 bg-primary/10 rounded-xl md:rounded-2xl">
                <Stethoscope className="text-primary w-5 h-5 md:w-6 md:h-6" />
              </div>
              <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">Clinical History</h3>
            </div>
            
            <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm p-1 md:p-2 overflow-hidden">
               <div className="max-h-[300px] md:max-h-[400px] overflow-y-auto scrollbar-hide px-3 py-3 md:p-4 space-y-2">
                 {historyData?.a_medical_history && historyData.a_medical_history.length > 0 ? (
                    historyData.a_medical_history.map((item, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-xl md:rounded-3xl bg-slate-50 border border-slate-100 flex justify-between items-center group hover:bg-white hover:border-primary/20 transition-all">
                        <div className="overflow-hidden">
                          <p className="text-xs md:text-sm font-black text-slate-800 mb-1 truncate">{item.values.condition.slug}</p>
                          <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">
                            Onset: {item.values.onset_date ? format(new Date(item.values.onset_date), 'dd MMM yyyy') : 'N/A'}
                          </p>
                        </div>
                        <div className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-widest shrink-0 ml-2 ${item.values.is_active == '1' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                          {item.values.is_active == '1' ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                    ))
                 ) : (
                    <div className="py-12 text-center">
                      <FileText className="mx-auto text-slate-200 mb-2 w-8 h-8 md:w-12 md:h-12" />
                      <p className="text-slate-400 font-bold uppercase text-[8px] md:text-[10px] tracking-widest">No Medical History</p>
                    </div>
                 )}
               </div>
            </div>
          </section>

          {/* Medications */}
          <section>
            <div className="flex items-center gap-4 mb-4 md:mb-6">
              <div className="p-2 bg-indigo-500/10 rounded-xl md:rounded-2xl">
                <FileText className="text-indigo-500 w-5 h-5 md:w-6 md:h-6" />
              </div>
              <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">Prescriptions</h3>
            </div>
            
            <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm p-1 md:p-2 overflow-hidden">
               <div className="max-h-[300px] md:max-h-[400px] overflow-y-auto scrollbar-hide px-3 py-3 md:p-4 space-y-3">
                 {healthData?.b_medicine && healthData.b_medicine.length > 0 ? (
                    healthData.b_medicine.map((item, idx) => (
                      <div key={idx} className="p-4 md:p-5 rounded-xl md:rounded-[2rem] bg-slate-50 border border-slate-100 group hover:bg-white hover:border-primary/20 transition-all">
                        <div className="flex justify-between items-start mb-2 md:mb-3 overflow-hidden">
                          <div className="overflow-hidden">
                            <p className="text-sm md:text-base font-black text-slate-900 truncate">{item.values.name.name}</p>
                            <p className="text-[10px] md:text-xs font-bold text-indigo-500 uppercase tracking-widest truncate">{item.values.type} • {item.values.strength}</p>
                          </div>
                          <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 font-black text-[10px] md:text-xs shrink-0 ml-2">
                            {idx + 1}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-200/50">
                          <div className="text-[9px] md:text-[10px] font-bold text-slate-400 overflow-hidden">
                             <span className="uppercase block mb-0.5">Sig</span>
                             <span className="text-slate-700 text-[10px] md:text-xs truncate block">{item.values.sig || 'As directed'}</span>
                          </div>
                          <div className="text-[9px] md:text-[10px] font-bold text-slate-400 text-right shrink-0">
                             <span className="uppercase block mb-0.5">Duration</span>
                             <span className="text-slate-700 text-[10px] md:text-xs">{item.values.intake_total_days} Days</span>
                          </div>
                        </div>
                      </div>
                    ))
                 ) : (
                    <div className="py-12 text-center">
                      <Zap className="mx-auto text-slate-200 mb-2 w-8 h-8 md:w-12 md:h-12" />
                      <p className="text-slate-400 font-bold uppercase text-[8px] md:text-[10px] tracking-widest">No Prescriptions</p>
                    </div>
                 )}
               </div>
            </div>
          </section>
        </div>

        {/* Complaints Section */}
        <section className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm p-6 md:p-8">
           <div className="flex items-center gap-4 mb-6 md:mb-8">
              <div className="p-2 bg-amber-500/10 rounded-xl md:rounded-2xl">
                <Info className="text-amber-500 w-5 h-5 md:w-6 md:h-6" />
              </div>
              <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">Chief Complaints & HPI</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
               <div>
                  <h4 className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 md:mb-4">Complaints</h4>
                  <div className="space-y-3 md:space-y-4">
                    {healthData?.a_chief_complaints && healthData.a_chief_complaints.length > 0 ? (
                      healthData.a_chief_complaints.map((item, idx) => (
                        <div key={idx} className="flex gap-3 p-3 md:p-4 rounded-2xl md:rounded-3xl bg-amber-50/50 border border-amber-100">
                           <div className="w-7 h-7 md:w-8 md:h-8 rounded-xl md:rounded-2xl bg-amber-500 text-white flex items-center justify-center font-black text-[10px] md:text-xs shrink-0">
                             {idx + 1}
                           </div>
                           <div>
                             <p className="font-black text-slate-900 text-sm mb-0.5">{item.addition_info?.title}</p>
                             <p className="text-xs md:text-sm text-slate-600 font-medium italic line-clamp-3">"{item.addition_info?.notes}"</p>
                           </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-xs italic">No complaints recorded.</p>
                    )}
                  </div>
               </div>

               <div>
                  <h4 className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 md:mb-4">Illness History (HPI)</h4>
                  {healthData?.d_hpi && healthData.d_hpi.length > 0 ? (
                    <div className="p-5 md:p-6 rounded-2xl md:rounded-[2rem] bg-slate-50 border border-slate-100">
                       <p className="text-slate-700 text-xs md:text-sm leading-relaxed font-medium mb-6">
                          {healthData.d_hpi[0].values.free_text || "History details recorded in assessment."}
                       </p>
                       <div className="grid grid-cols-2 gap-4">
                          <div className="text-[9px] md:text-[10px] font-bold text-slate-400 overflow-hidden">
                             <span className="uppercase block mb-1">Onset</span>
                             <span className="text-slate-900 font-black truncate block">{healthData.d_hpi[0].values.date || 'N/A'}</span>
                          </div>
                          <div className="text-[9px] md:text-[10px] font-bold text-slate-400 text-right">
                             <span className="uppercase block mb-1">Severity</span>
                             <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-600 font-black uppercase text-[8px] inline-block">{healthData.d_hpi[0].values.severity || 'N/A'}</span>
                          </div>
                       </div>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-xs italic">No HPI data recorded.</p>
                  )}
               </div>
            </div>
        </section>
      </main>

      {/* Footer Branding */}
      <footer className="max-w-7xl mx-auto px-6 py-12 flex flex-col items-center">
         <div className="h-px w-24 bg-slate-200 mb-8" />
         <div className="flex items-center gap-2 opacity-30 grayscale mb-2">
            <ShieldCheck size={18} />
            <span className="text-base md:text-lg font-black tracking-tight">TeleGaruda</span>
         </div>
         <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Clinical-Grade Portal • © 2024</p>
      </footer>
    </div>

      {/* Ambient AI Assistant — mounted here, opens via 'open-ambient-ai' event */}
      {subscriberPatientId && <AmbientAssistant patientId={subscriberPatientId ?? undefined} />}
    </>
  );
};

export default TeleconsultSummary;